/**
 * Swym Wishlist Integration for Depict Product Cards
 * 
 * This script dynamically inserts wishlist buttons into Depict product cards
 * and handles the initialization of Swym wishlist functionality.
 */

(function () {
  'use strict';

  /**
   * Creates a wishlist button element for a given product
   * @param {string} productId - The Shopify product ID
   * @param {string} variantId - The Shopify variant ID
   * @param {string} productUrl - The product URL
   * @returns {HTMLElement} The wishlist button element
   */
  function createWishlistButton(productId, variantId, productUrl) {
    const button = document.createElement('button');
    button.setAttribute('aria-label', 'Add to Wishlist');
    button.setAttribute('data-with-epi', 'true');
    button.className = 'swym-button swym-add-to-wishlist-view-product product_' + productId;
    button.setAttribute('data-swaction', 'addToWishlist');
    button.setAttribute('data-product-id', productId);
    button.setAttribute('data-variant-id', variantId);
    button.setAttribute('data-product-url', productUrl);

    // Add wishlist icon
    button.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M5 6.2C5 5.07989 5 4.51984 5.21799 4.09202C5.40973 3.71569 5.71569 3.40973 6.09202 3.21799C6.51984 3 7.07989 3 8.2 3H15.8C16.9201 3 17.4802 3 17.908 3.21799C18.2843 3.40973 18.5903 3.71569 18.782 4.09202C19 4.51984 19 5.07989 19 6.2V21L12 16L5 21V6.2Z" stroke="#000000" stroke-width="1.4px" fill="none" stroke-linejoin="round"></path>' +
      '</svg>';

    return button;
  }

  /**
   * Inserts a wishlist button into a product card if it doesn't already have one
   * @param {HTMLElement} card - The product card element
   * @returns {boolean} True if button was inserted, false otherwise
   */
  function insertWishlistButton(card) {
    // Check if button already exists
    if (card.querySelector('.swym-button')) {
      return false;
    }

    // Find the image container
    const imageContainer = card.querySelector('.image-container');
    if (!imageContainer) {
      return false;
    }

    // Extract product data
    const productData = {
      productId: card.getAttribute('data-product-id'),
      variantId: card.getAttribute('data-variant-id'),
      productUrl: card.getAttribute('data-product-url')
    }
    if (!productData.productId || !productData.variantId || !productData.productUrl) {
      console.log('Swym: Missing product data attributes on card');
      return false;
    }

    // Create and insert the button as the first child
    const button = createWishlistButton(
      productData.productId,
      productData.variantId,
      productData.productUrl
    );
    imageContainer.insertBefore(button, imageContainer.firstChild);

    return true;
  }

  /**
   * Processes all Depict product cards and inserts wishlist buttons
   * @returns {number} Number of buttons inserted
   */
  function processAllProductCards() {
    const cards = document.querySelectorAll('.depict-product-card');
    let insertedCount = 0;

    cards.forEach(function (card) {
      if (insertWishlistButton(card)) {
        insertedCount++;
      }
    });

    return insertedCount;
  }

  /**
   * Observer to detect when new Depict product cards are added to the DOM
   * Handles both collection product grids and recommendation sliders
   */
  function initProductCardObserver() {
    // Track which containers we've already initialized
    const initializedContainers = new Set();

    /**
     * Sets up observer for a specific container
     */
    function observeContainer(container, containerType) {
      // Skip if already initialized
      if (initializedContainers.has(container)) {
        return;
      }
      initializedContainers.add(container);

      // Process initial products in this container
      const initialCount = processAllProductCards();
      if (initialCount > 0) {
        console.log('Swym: Processed', initialCount, 'initial product cards in', containerType);
        // Dispatch event to initialize Swym buttons
        document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
      }

      // Create a MutationObserver to watch for new products being added
      const observer = new MutationObserver(function (mutations) {
        let shouldDispatchEvent = false;

        mutations.forEach(function (mutation) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes are product cards
            mutation.addedNodes.forEach(function (node) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the node itself is a product card
                if (node.classList && node.classList.contains('depict-product-card')) {
                  if (insertWishlistButton(node)) {
                    shouldDispatchEvent = true;
                  }
                }
                // Also check for product cards within the added node
                const nestedCards = node.querySelectorAll ? node.querySelectorAll('.depict-product-card') : [];
                nestedCards.forEach(function (card) {
                  if (insertWishlistButton(card)) {
                    shouldDispatchEvent = true;
                  }
                });
              }
            });
          }
        });

        // Dispatch event if any new buttons were added
        if (shouldDispatchEvent) {
          console.log('Swym: New product cards detected in', containerType, ', reinitializing buttons');
          document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
        }
      });

      // Start observing the products container
      observer.observe(container, {
        childList: true,
        subtree: true
      });

      console.log('Swym: Product card observer initialized for', containerType);
    }

    /**
     * Checks for both types of containers
     */
    function checkForContainers() {
      // Check for collection product grid containers (.cards)
      const cardsContainers = document.querySelectorAll('.PLP-results .cards');
      cardsContainers.forEach(function (container) {
        observeContainer(container, 'collection grid (.cards)');
      });

      // Check for recommendation slider containers (.sliding)
      // Both on PLP (.PLP-results) and PDP (.depict-recommendations)
      const slidingContainers = document.querySelectorAll('.PLP-results .sliding, .depict-recommendations .sliding');
      slidingContainers.forEach(function (container) {
        observeContainer(container, 'recommendation slider (.sliding)');
      });

      return cardsContainers.length + slidingContainers.length;
    }

    // Check for containers immediately
    const foundContainers = checkForContainers();

    // If no containers found yet, set up polling
    if (foundContainers === 0) {
      const checkInterval = setInterval(function () {
        const found = checkForContainers();
        if (found > 0) {
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup check after 10 seconds if containers are not found
      setTimeout(function () {
        clearInterval(checkInterval);
      }, 10000);
    }

    // Also set up a MutationObserver on the document to catch dynamically added containers
    // (for cases where recommendation sliders are added after page load)
    const documentObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any new containers were added
          checkForContainers();
        }
      });
    });

    // Observe both .PLP-results (collection pages) and .depict-recommendations (PDP) containers
    const plpResults = document.querySelector('.PLP-results');
    if (plpResults) {
      documentObserver.observe(plpResults, {
        childList: true,
        subtree: true
      });
    }

    const depictRecommendations = document.querySelector('.depict-recommendations');
    if (depictRecommendations) {
      documentObserver.observe(depictRecommendations, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Swym callback function - initializes buttons when Swym is ready
   */
  function swymCallbackFn(swat) {
    // Listen for the custom event dispatched when buttons are rendered
    document.addEventListener('swym:collections-loaded', function () {
      // Initialize buttons in both collection grids and recommendation sliders
      swat.initializeActionButtons('.cards');
      swat.initializeActionButtons('.sliding');
      console.log('Swym: Action buttons initialized for collection grids and recommendation sliders');
    });
  }

  // Register the Swym callback
  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }
  window.SwymCallbacks.push(swymCallbackFn);

  // Initialize the observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardObserver);
  } else {
    initProductCardObserver();
  }

})();
